import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BArecordMark } from "@/components/BArecordMark";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Services", to: "/services" },
  { label: "Solutions", to: "/solutions" },
  { label: "Pricing", to: "/pricing" },
  { label: "Trust", to: "/trust" },
  { label: "Resources", to: "/resources" },
  { label: "Contact", to: "/contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (to) => pathname === to || pathname.startsWith(to + "/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setBannerVisible(localStorage.getItem("barecord_launch_banner") !== "dismissed");
    const onChange = (e) => setBannerVisible(!!e.detail?.visible);
    window.addEventListener("barecord-banner-change", onChange);
    return () => window.removeEventListener("barecord-banner-change", onChange);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const goPortal = () => {
    if (!user) return navigate("/login");
    const dest = user.role === "admin" ? "/admin" : user.role === "partner" ? "/partner" : "/client";
    navigate(dest);
  };

  return (
    <>
      <a href="#main-content" className="skip-link" data-testid="skip-link">Skip to content</a>
      <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed ${bannerVisible ? "top-10" : "top-0"} inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <Logo />
        </a>

        <div className="hidden md:flex items-center gap-9">
          <div className="relative group" data-testid="nav-products-menu">
            <button
              data-testid="nav-products"
              className="text-sm text-fog hover:text-paper transition-colors duration-300 inline-flex items-center gap-1"
            >
              Products
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="w-72 rounded-2xl border border-white/10 bg-black/90 backdrop-blur-xl p-2 shadow-2xl">
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-fog/60">Aliwer products</p>
                <Link
                  to="/barecord"
                  data-testid="nav-products-barecord"
                  className="flex items-start justify-between gap-2 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-colors"
                >
                  <span className="flex items-start gap-2.5">
                    <BArecordMark size={16} className="text-paper mt-0.5" />
                    <span>
                      <span className="block text-sm text-paper">BArecord</span>
                      <span className="block text-xs text-fog">AI communications archive</span>
                    </span>
                  </span>
                  <span className="mt-0.5 shrink-0 text-[9px] uppercase tracking-widest bg-white/10 text-paper rounded-full px-1.5 py-0.5">Early access</span>
                </Link>
                <Link
                  to="/products/securedesk"
                  data-testid="nav-products-securedesk"
                  className="flex items-start justify-between gap-2 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-colors"
                >
                  <span>
                    <span className="block text-sm text-paper">SecureDesk</span>
                    <span className="block text-xs text-fog">AI support &amp; security ops</span>
                  </span>
                  <span className="mt-0.5 shrink-0 text-[9px] uppercase tracking-widest border border-white/20 text-fog rounded-full px-1.5 py-0.5">Waitlist · Nov 7</span>
                </Link>
              </div>
            </div>
          </div>
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              aria-current={isActive(l.to) ? "page" : undefined}
              className={`text-sm transition-colors duration-300 ${
                isActive(l.to)
                  ? "text-paper border-b border-paper/70 pb-0.5"
                  : "text-fog hover:text-paper"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
            aria-expanded={open}
            className="md:hidden p-2 -mr-1 text-paper hover:opacity-70 transition-opacity"
          >
            <Menu size={22} />
          </button>
          {user ? (
            <>
              <button
                onClick={goPortal}
                data-testid="nav-portal-btn"
                className="hidden md:inline text-sm text-fog hover:text-paper transition-colors"
              >
                {user.role === "admin" ? "Admin Dashboard" : user.role === "partner" ? "Partner Portal" : "Client Portal"}
              </button>
              <button
                onClick={logout}
                data-testid="nav-logout-btn"
                className="hidden md:inline-flex rounded-full bg-paper text-black text-sm font-medium px-5 py-2 hover:bg-white transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="hidden md:inline text-sm text-fog hover:text-paper transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/register"
                data-testid="nav-getstarted-link"
                className="hidden md:inline-flex rounded-full bg-paper text-black text-sm font-medium px-5 py-2 hover:bg-white transition-colors"
              >
                Get started
              </Link>
            </>
          )}
        </div>
      </nav>
    </motion.header>

      {/* Mobile menu — full-screen overlay that replaces the header */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[80] bg-black overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="h-16 px-6 flex items-center justify-between border-b border-white/10">
            <Logo onClick={() => { setOpen(false); navigate("/"); }} />
            <button
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Close menu"
              className="p-2 -mr-1 text-paper hover:opacity-70 transition-opacity"
            >
              <X size={22} />
            </button>
          </div>
          <div className="px-6 py-5 space-y-1">
            <p className="px-1 pt-1 pb-2 text-[10px] uppercase tracking-widest text-fog/70">Products</p>
            <Link to="/barecord" onClick={() => setOpen(false)} data-testid="mobile-nav-barecord" className="flex items-center justify-between rounded-xl px-3 py-3 text-paper hover:bg-white/5 transition-colors">
              <span className="flex items-center gap-2.5">
                <BArecordMark size={16} className="text-paper" />
                <span className="text-base">BArecord</span>
              </span>
              <span className="text-[9px] uppercase tracking-widest bg-white/10 rounded-full px-1.5 py-0.5">Early access</span>
            </Link>
            <Link to="/products/securedesk" onClick={() => setOpen(false)} data-testid="mobile-nav-securedesk" className="flex items-center justify-between rounded-xl px-3 py-3 text-paper hover:bg-white/5 transition-colors">
              <span className="text-base">SecureDesk</span>
              <span className="text-[9px] uppercase tracking-widest border border-white/20 text-fog rounded-full px-1.5 py-0.5">Waitlist · Nov 7</span>
            </Link>

            <div className="h-px bg-white/10 my-3" />
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                aria-current={isActive(l.to) ? "page" : undefined}
                className={`block rounded-xl px-3 py-3 text-base transition-colors ${
                  isActive(l.to) ? "text-paper bg-white/5" : "text-paper hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            ))}

            <div className="h-px bg-white/10 my-3" />
            {user ? (
              <>
                <button
                  onClick={() => { setOpen(false); goPortal(); }}
                  data-testid="mobile-nav-portal"
                  className="block w-full text-left rounded-xl px-3 py-3 text-base text-paper hover:bg-white/5 transition-colors"
                >
                  {user.role === "admin" ? "Admin Dashboard" : user.role === "partner" ? "Partner Portal" : "Client Portal"}
                </button>
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  data-testid="mobile-nav-logout"
                  className="mt-1 w-full rounded-full bg-paper text-black text-sm font-medium px-5 py-3 hover:bg-white transition-colors"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login" className="block rounded-xl px-3 py-3 text-base text-paper hover:bg-white/5 transition-colors">
                  Log in
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} data-testid="mobile-nav-getstarted" className="mt-1 block text-center rounded-full bg-paper text-black text-sm font-medium px-5 py-3 hover:bg-white transition-colors">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
